import { UploadStatus } from "@/types/dataset";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { Store } from "pinia";
import i18n from "./i18n";
export default class Upload {
	api;
	totalUploadedList = {} as any;
	chunkUploaded = {} as any;
	failedList = {} as any;
	file: File | { name: string } | null = null;
	type = "";
	uploading = false;
	progress = 0;
	finished: ((arg: string) => string | number) | null = null;
	hasError = false;
	status = "";
	metadata: object | null = null;
	blobName = "";
	siteId = "";
	fileId = "";
	groupName = "";
	folderPath = "";
	cacheKeyUploadedChunks = "";
	datasetType = "";
	datasetName = "";
	constructor(
		item: { file: any; progress: any; type?: any; status?: any },
		datasetName: string,
		groupName: string,
		folderPath: string,
		api: Store<
			"apiStore",
			{
				cancel: () => Promise<void>;
				post: (
					arg: { endpoint: string; post: unknown },
					config: AxiosRequestConfig
				) => Promise<AxiosResponse>;
				put: (
					arg: { endpoint: string; post: unknown },
					config?: AxiosRequestConfig
				) => Promise<AxiosResponse>;
				delete: (arg: string) => Promise<AxiosResponse>;
				get: (arg: string) => Promise<AxiosResponse>;
			}
		>,
		finishedCallback: any,
		siteId: string
	) {
		this.file = item.file;
		if (item.file.fileId) this.fileId = item.file.fileId;

		this.datasetName = datasetName;
		this.groupName = groupName;
		this.folderPath = folderPath;
		this.type = item.type;
		this.status = item.file.status;
		this.siteId = siteId;
		this.api = api;
		this.finished = finishedCallback;
		if (
			this.type == "collar" ||
			this.type == "survey" ||
			this.type == "trajectory" ||
			this.type == "planned" ||
			this.type == "point" ||
			this.type == "interval"
		) {
			this.datasetType = "Drillhole";
		}
		if (this.type == "shape") {
			this.datasetType = "Surface";
		}
		if (this.type == "blockmodel") {
			this.datasetType = "Blockmodel";
		}
		if (this.type == "pointdata") {
			this.datasetType = "Points";
		}
	}
	async cancel() {
		if (this.progress != 1 && this.uploading) {
			try {
				await this.api.delete(`/upload-dataset/delete?rawFileId=${this.fileId}`);
			} catch (e) {
				console.log(e);
			}
		}
		this.file = null;
		this.totalUploadedList = {};
		this.chunkUploaded = {};
		this.uploading = false;
		this.progress = 0;
		await this.api.cancel();
	}
	async getFileMeta() {
		try {
			localStorage.removeItem(this.cacheKeyUploadedChunks);

			if (this.finished && this.file && this.fileId) {
				if (this.type != "shape") {
					const { data } = await this.api.put({
						endpoint: `/dataset-metadata/update`,
						post: { id: this.fileId }
					});
					this.metadata = data;
					this.progress = 1;
					this.status = i18n.global.t("uploadHelper.retrievedMetadata");
					this.finished(this.file.name);
				} else {
					// HANDLE SHAPES (BLOCKMODELS now require metadata to be queriable first)
					this.status = i18n.global.t("uploadHelper.uploadedSuccess");
					this.progress = 1;
					this.finished(this.file.name);
				}
			} else {
				throw new Error();
			}
		} catch (e) {
			if (e instanceof AxiosError) {
				localStorage.removeItem(this.cacheKeyUploadedChunks);
				this.hasError = true;
				this.status = e.response!.data.message;
				if (this.finished && this.file) this.finished(this.file.name);
			} else {
				console.log(e);
				localStorage.removeItem(this.cacheKeyUploadedChunks);
				this.hasError = true;
			}
		}
	}

	async begin(delay = 0) {
		try {
			await new Promise((resolve) => setTimeout(resolve, delay * 250)); // delay to prevent simultaneous requests
			await this.api.post(
				{
					endpoint: "/dataset-metadata/validate",
					post: {
						datasetName: this.datasetName,
						groupName: this.groupName, // future support
						datasetType: this.datasetType,
						site: this.siteId,
						folderPath: this.folderPath
					}
				},
				{}
			);
		} catch (error: any) {
			this.hasError = true;
			this.status = error.response.data.message;
			if (this.finished && this.file) this.finished(this.file.name);
			console.error("Error on request", error.request);
			return;
		}

		if (
			this.status == i18n.global.t("uploadHelper.metadataAvailable") ||
			this.status == i18n.global.t("uploadHelper.noMetadata")
		) {
			this.getFileMeta();
			this.status = i18n.global.t("uploadHelper.retrievingMetadata");
		} else {
			this.uploading = true;
			this.createChunks(0);
		}
	}
	async createChunks(fileIndex: number, retryChunksList: number[] | null = null) {
		const file: any = this.file;

		if (!file) {
			console.error("File is null or undefined");
			return;
		}

		const fileName = file.name;
		const relativePath =
			file.webkitRelativePath || file.relativePath || file.fileName || file.name;
		const uniqueIdentifier =
			file.size +
			"-" +
			relativePath.replace(/[^0-9a-zA-Z_-]/gim, "") +
			"." +
			fileName.split(".").pop();

		this.cacheKeyUploadedChunks = uniqueIdentifier + "-cacheKeyUploadedCount";

		// Add 3.5 MB for each chunk
		const chunkSize = 3.5 * 1024 * 1024;

		const chunkCount = Math.ceil(file.size / chunkSize);
		this.totalUploadedList[uniqueIdentifier] = [];

		const chunksToUpload = retryChunksList ?? [...Array(chunkCount - 1).keys()]; // equivalent to python range(chunkCount) - decrementing by -1 to prevent early serverless merge

		// skip cache check if not retrying
		if (retryChunksList == null) {
			// const existing: number[] = JSON.parse(
			// 	localStorage.getItem(this.cacheKeyUploadedChunks) || "[]"
			// );
			// // filter out the previously uploaded chunks
			// chunksToUpload = chunksToUpload.filter(
			// 	(chunkNumber: number) => !existing.includes(chunkNumber + 1)
			// );
		}

		const promises: Promise<{ chunkNumber: number; status: string } | undefined>[] = [];
		for (const i of chunksToUpload) {
			const start = i * chunkSize;
			const end = Math.min(start + chunkSize, file.size);

			const chunk = file.slice(start, end);

			promises.push(
				this.uploadChunk(
					retryChunksList ? i : i + 1, // this is silly and should get refactored out, but the serverless function needs indexes starting at 1
					chunk,
					chunkCount,
					fileName,
					uniqueIdentifier,
					fileIndex
				)
			);
		}

		await this.promiseAllSettledRateLimiter(promises).then(async (results: any) => {
			const allResValues = (
				results.filter(
					(
						c: PromiseFulfilledResult<
							{ chunkNumber: number; status: string } | undefined
						>
					) => c.status === "fulfilled"
				) as PromiseFulfilledResult<{ chunkNumber: number; status: string } | undefined>[]
			).map((c) => c.value);

			const failedResults = (
				results.filter(
					(c: PromiseRejectedResult) => c.status === "rejected"
				) as PromiseRejectedResult[]
			).map((c) => c.reason);

			const failedChunks = [];

			const uploaded = JSON.parse(localStorage.getItem(this.cacheKeyUploadedChunks) || "[]");

			for (const res of allResValues) {
				if (!uploaded.includes(res?.chunkNumber)) {
					uploaded.push(res?.chunkNumber);
				}
			}

			for (const res of failedResults) {
				if (
					Array.isArray(uploaded) &&
					uploaded.findIndex((item) => item === res?.chunkNumber) > -1
				) {
					uploaded.splice(res?.chunkNumber, 1);
				}
				failedChunks.push(res?.chunkNumber);
			}

			localStorage.setItem(this.cacheKeyUploadedChunks, JSON.stringify(uploaded));

			if (failedChunks.length == 0) {
				// send the last chunk and merge only when all the other chunks have been uploaded succesfully
				const lastChunk = chunkCount - 1;
				const start = lastChunk * chunkSize;
				const end = Math.min(start + chunkSize, file.size);
				const chunk = file.slice(start, end);

				await this.uploadChunk(
					lastChunk + 1, // increment again, since the chunking uses 1-based indexing but the splitting uses 0
					chunk,
					chunkCount,
					fileName,
					uniqueIdentifier,
					fileIndex
				);
			} else {
				return this.createChunks(fileIndex, failedChunks);
			}
		});
	}
	async promiseAllSettledRateLimiter(promises: Promise<any>[]) {
		const MAX_CONCURRENT_REQUESTS = 4;
		const chunks = promises.slice(); // create a copy of the array of promises
		const results = []; // array to store the results
		while (chunks.length > 0) {
			const batch = chunks.splice(0, MAX_CONCURRENT_REQUESTS); // get a batch of promises
			const batchResults = await Promise.allSettled(batch); // execute the promises in the batch concurrently
			results.push(...batchResults); // add the batch results to the array of results
			await new Promise((resolve) => setTimeout(resolve, 1000)); // wait for 1 second before executing the next batch
		}
		return results;
	}
	async checkFinalizeUpload(datasetId: string, retryAttempt = 0) {
		const retryTimeout = 7000;
		const maxRetries = 350;
		// ideally this would watch an event, but polling works since we don't have access to event store in this context
		try {
			const { data } = await this.api.put({
				endpoint: `/dataset-metadata/update`,
				post: { id: datasetId }
			});

			if (data.errorMsg) {
				if (retryAttempt > maxRetries) {
					this.hasError = true;
					this.status = i18n.global.t("uploadHelper.retriesTimeout");
					return;
				}
				setTimeout(
					async () => await this.checkFinalizeUpload(datasetId, ++retryAttempt),
					retryTimeout
				);
				return;
			}

			if (
				data.status === ("Complete" as UploadStatus) &&
				((data?.columns && data?.columns.length > 0) || this.type == "shape")
			) {
				this.getFileMeta();
				return;
			} else {
				this.hasError = true;
				this.status = i18n.global.t("uploadHelper.somethingWentWrongFinalizing");
				return;
			}
		} catch (error: any) {
			if (error?.response?.data?.message) {
				this.hasError = true;
				this.status = error.response.data.message;
				return;
			} else {
				this.hasError = true;
				this.status = i18n.global.t("uploadHelper.errorGettingStatus");
				return;
			}
		}
	}
	async uploadChunk(
		i: number,
		chunk: any,
		chunkCount: number,
		fileName: string,
		uniqueIdentifier: string,
		fileIndex = 0
	) {
		try {
			const response = await this.api.post(
				{
					endpoint: "/upload-dataset/create",
					post: chunk
				},
				{
					params: {
						name: this.datasetName,
						groupName: this.groupName,
						folderPath: this.folderPath,
						datasetType: this.datasetType,
						chunkNumber: i,
						chunkTotalCount: chunkCount,
						fileName,
						uniqueIdentifier,
						site: this.siteId
					},
					headers: {
						"Content-Type": "application/octet-stream"
					}
				}
			);

			// Error handling
			console.log("Upload status ==============> ", response.status);
			if (response.status === 200) {
				console.log("Chunk uploaded successfully");
				this.totalUploadedList[uniqueIdentifier].push(i + 1);
				if (response.data.is_completed) {
					this.progress = 1;
					this.status = i18n.global.t("uploadHelper.retrievingMetadataWait");
					// this.blobName = response.data.file_name;
					this.fileId = response.data.id;
					await this.checkFinalizeUpload(response.data.id); // calls   await this.getFileMeta();
					return { chunkNumber: i, status: "success" };
				} else if (response.data.pendingLongUpload) {
					this.progress = 1;
					this.status = `${
						fileName.split(".").pop() === "zip"
							? i18n.global.t("uploadHelper.unpackingZip")
							: i18n.global.t("uploadHelper.mergingChunks")
					}`;
					this.fileId = response.data.id;

					// wait for event; polling for status from api as event isn't accessible here
					await this.checkFinalizeUpload(response.data.id); // calls   await this.getFileMeta();
					return { chunkNumber: i, status: "success" };
				} else {
					this.fileId = response.data.id;
					this.progress = Number(
						(this.totalUploadedList[uniqueIdentifier].length / chunkCount) * 1
					);
					return { chunkNumber: i, status: "success" };
				}
			}
			if (response.status === 400) {
				this.hasError = true;
				if (this.finished && this.file) this.finished(this.file?.name);
				localStorage.removeItem(this.cacheKeyUploadedChunks);
				this.status = response.data.message;
			}
		} catch (error: any) {
			if (error.response) {
				if (error.response.status == 412) {
					this.createChunks(fileIndex, error.response.data.message);
				} else {
					localStorage.removeItem(this.cacheKeyUploadedChunks);
					(this.finished as any)((this.file as any).name);
					console.error("Error on request, status: ", error.response.status);
					this.hasError = true;
					this.status = error.response.data.message;
				}
			} else if (error.request) {
				this.hasError = true;
				this.status = error.response.data.message;
				localStorage.removeItem(this.cacheKeyUploadedChunks);
				// The request was made but no response was received
				// `error.request` is an instance of XMLHttpRequest in the browser
				console.error("Error on request", error.request);
			} else {
				this.hasError = true;
				this.status = error.message;
				localStorage.removeItem(this.cacheKeyUploadedChunks);
				// Something happened in setting up the request that triggered an Error
				console.error("Error setting up request: ", error.message);
			}
			console.error(error.config);
		}
	}
}
